import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { font, Button, Input, Textarea, Panel, Badge, Avatar, ProgressBar } from "@realtyflows/ui";
import { useTheme } from "../contexts/ThemeContext";
import { getClient, updateClient, deleteClient, getClientDeals, updateDeal, deleteDeal, getMyTeam, assignClient, updateEngagementPattern } from "../api";
import { stageColors } from "../data/mockData";
import ConfirmModal from "../components/ConfirmModal";
import AIPanel from "../components/AIPanel";
import DealDocumentTimeline from "../components/DealDocumentTimeline";
import ClientDocuments from "../components/ClientDocuments";
import ScoreHistoryChart from "../components/ScoreHistoryChart";
import MarketContext from "../components/MarketContext";
import useResponsive from "../hooks/useResponsive";

export default function ClientDetail() {
  const t = useTheme();  // V2 ADR-003 Phase 4.3 wave 8
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("clientId");
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(searchParams.get("edit") === "true");
  const [editForm, setEditForm] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeTab, setActiveTab] = useState("overview"); // overview | deals | documents | ai
  const [expandedDeal, setExpandedDeal] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const responsive = useResponsive();

  const fetchData = useCallback(async () => {
    try {
      const [l, d] = await Promise.all([getClient(clientId), getClientDeals(clientId)]);
      setClient(l);
      setDeals(d);
      setEditForm({ name: l.name, email: l.email, phone: l.phone, location: l.location, notes: l.notes || "" });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const storedUser = localStorage.getItem("realtyflows_token");
    if (storedUser) {
      const userId = JSON.parse(atob(storedUser.split(".")[1])).id;
      getMyTeam(userId).then(data => {
        if (data.members?.length > 0) setTeamMembers(data.members);
      }).catch(() => {});
    }
  }, []);

  const handleAssign = async (agentId) => {
    try { await assignClient({ client_id: clientId, agent_id: agentId || null }); fetchData(); } catch (err) { console.error(err); }
  };

  const handleUpdate = async () => {
    try { await updateClient(clientId, editForm); setEditing(false); fetchData(); } catch (err) { console.error(err); }
  };

  const handleDeleteClient = async () => {
    try { await deleteClient(clientId); navigate("/clients"); } catch (err) { console.error(err); }
  };

  const handleDealStatusUpdate = async (dealId, status) => {
    try { await updateDeal(dealId, { status }); setDeals(await getClientDeals(clientId)); } catch (err) { console.error(err); }
  };

  const handleStageApply = async (stage) => {
    try { await updateClient(clientId, { status: stage }); fetchData(); } catch (err) { console.error(err); }
  };

  const handleDeleteDeal = async (dealId) => {
    try { await deleteDeal(dealId); setDeals(await getClientDeals(clientId)); } catch (err) { console.error(err); }
  };

  const patternLabels = {
    ghost_post_showing: "Ghost (Post-Showing)", ghost_pre_showing: "No-Show",
    ghost_post_offer: "Ghost (Post-Offer)", seasonal_pauser: "Seasonal Pauser",
    slow_burner: "Slow Burner", referred_out: "Referred Out", active: "Active",
  };
  const patternColors = {
    ghost_post_showing: t.coral, ghost_pre_showing: t.coral, ghost_post_offer: "#e74c3c",
    seasonal_pauser: t.amber, slow_burner: t.sky, referred_out: t.textMuted, active: t.emerald,
  };
  const patternDescriptions = {
    ghost_post_showing: "Client went silent 14+ days after completing a showing.",
    ghost_pre_showing: "Client had a confirmed appointment but never showed up.",
    ghost_post_offer: "No response 7+ days after an offer was submitted.",
    seasonal_pauser: "History of periodic engagement gaps — likely to return.",
    slow_burner: "Low-frequency engagement, same deal stage for 60+ days.",
    referred_out: "Manually referred to another agent. Excluded from pipeline.",
    active: "Client is actively engaged.",
  };

  const handlePatternChange = async (pattern, reason) => {
    try { await updateEngagementPattern(clientId, { pattern, reason }); fetchData(); } catch (err) { console.error(err); }
  };

  if (loading || !client) return <div style={{ color: t.textMuted, padding: 40, textAlign: "center", fontFamily: font.body }}>Loading...</div>;

  const scoreColor = client.score >= 80 ? t.emerald : client.score >= 50 ? t.amber : t.coral;
  const engColor = patternColors[client.engagement_pattern] || t.emerald;
  const daysSinceCreated = Math.floor((Date.now() - new Date(client.created_at).getTime()) / 86400000);

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", maxWidth: 1200, margin: "0 auto" }}>
      {/* Back */}
      <button onClick={() => navigate("/clients")} style={{
        display: "flex", alignItems: "center", gap: 6, background: "none", border: "none",
        cursor: "pointer", color: t.textSecondary, fontSize: 13, fontFamily: font.body, padding: 0, marginBottom: 20,
      }}
        onMouseEnter={e => e.currentTarget.style.color = t.emerald}
        onMouseLeave={e => e.currentTarget.style.color = t.textSecondary}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back to Clients
      </button>

      {/* ═══ HERO HEADER ═══ */}
      <div style={{
        background: t.surface, border: `1px solid ${t.border}`, borderRadius: 14,
        padding: "24px 28px", marginBottom: 20, position: "relative",
      }}>
        {/* Three-dot menu — top right */}
        <div style={{ position: "absolute", top: 16, right: 16 }}>
          <div onClick={() => setMenuOpen(menuOpen === "hero" ? false : "hero")} style={{
            width: 32, height: 32, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", background: menuOpen === "hero" ? t.surfaceWarm : "transparent", border: `1px solid ${menuOpen === "hero" ? t.border : "transparent"}`,
            transition: "all 0.15s",
          }}
            onMouseEnter={e => { if (menuOpen !== "hero") e.currentTarget.style.background = t.surfaceWarm; }}
            onMouseLeave={e => { if (menuOpen !== "hero") e.currentTarget.style.background = "transparent"; }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="3" r="1.5" fill={t.textSecondary} />
              <circle cx="8" cy="8" r="1.5" fill={t.textSecondary} />
              <circle cx="8" cy="13" r="1.5" fill={t.textSecondary} />
            </svg>
          </div>
          {menuOpen === "hero" && (
            <>
              <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setMenuOpen(false)} />
              <div style={{
                position: "absolute", top: 38, right: 0, background: t.surface,
                border: `1.5px solid ${t.border}`, borderRadius: 10, padding: "6px 0",
                minWidth: 180, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", zIndex: 100,
                animation: "scaleIn 0.15s ease",
              }}>
                <div onClick={() => { setEditing(!editing); setMenuOpen(false); }}
                  style={{ padding: "9px 16px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13, fontFamily: font.body, color: t.textPrimary, transition: "background 0.1s" }}
                  onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Edit Client
                </div>
                <div style={{ height: 1, background: t.border, margin: "4px 0" }} />
                <div onClick={() => { setConfirmDelete({ type: "client", id: client.id, name: client.name }); setMenuOpen(false); }}
                  style={{ padding: "9px 16px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13, fontFamily: font.body, color: t.coral, transition: "background 0.1s" }}
                  onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={t.coral} strokeWidth="2"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                  Delete Client
                </div>
              </div>
            </>
          )}
        </div>

        {/* Top row: Avatar + Name + Score */}
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <Avatar name={client.name} size={60} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
              <span style={{ fontSize: 22, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>{client.name}</span>
              <Badge variant={client.status === "Closed" ? "emerald" : client.status === "Contacted" ? "sky" : client.status === "Showing Scheduled" ? "amber" : client.status === "Offer Submitted" ? "purple" : "default"}>
                {client.status}
              </Badge>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 5,
                background: engColor + "12", color: engColor, border: `1px solid ${engColor}30`,
                fontFamily: font.mono,
              }}>
                {patternLabels[client.engagement_pattern] || "Active"}
              </span>
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: 12, color: t.textMuted, fontFamily: font.body, flexWrap: "wrap" }}>
              {client.email && (
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  {client.email}
                </span>
              )}
              {client.phone && (
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  {client.phone}
                </span>
              )}
              {client.location && (
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  {client.location}
                </span>
              )}
            </div>
          </div>

          {/* Score Circle */}
          <div style={{ textAlign: "center", flexShrink: 0, marginRight: 36 }}>
            <div style={{
              width: 56, height: 56, borderRadius: "50%",
              background: `conic-gradient(${scoreColor} ${(client.score || 0) * 3.6}deg, ${t.surfaceWarm} 0deg)`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%", background: t.surface,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, fontWeight: 800, color: scoreColor, fontFamily: font.mono,
              }}>{client.score || 0}</div>
            </div>
            <div style={{ fontSize: 9, color: t.textMuted, fontFamily: font.mono, fontWeight: 600, marginTop: 3 }}>AI SCORE</div>
          </div>
        </div>

        {/* Bottom row: Quick Stats */}
        <div style={{ display: "flex", gap: 10, marginTop: 18, paddingTop: 16, borderTop: `1px solid ${t.border}` }}>
          {[
            { label: "Deals", value: deals.length, color: t.emerald },
            { label: "Source", value: client.source || "-", color: t.sky },
            { label: "Days", value: daysSinceCreated, color: t.amber },
          ].map(s => (
            <div key={s.label} style={{
              background: t.surfaceWarm, borderRadius: 8, padding: "8px 14px",
              textAlign: "center", border: `1px solid ${t.border}`, flex: 1,
            }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: s.color, fontFamily: font.mono }}>{s.value}</div>
              <div style={{ fontSize: 8, color: t.textMuted, fontFamily: font.mono, fontWeight: 600, letterSpacing: 0.5 }}>{s.label.toUpperCase()}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══ TAB BAR ═══ */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: `2px solid ${t.border}` }}>
        {[
          { key: "overview", label: "Overview" },
          { key: "deals", label: `Deals (${deals.length})` },
          { key: "documents", label: "Documents" },
          { key: "ai", label: "AI Assistant" },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: "10px 24px", fontSize: 13, fontWeight: activeTab === tab.key ? 700 : 400,
            color: activeTab === tab.key ? t.emerald : t.textSecondary,
            background: "none", border: "none", cursor: "pointer",
            borderBottom: `3px solid ${activeTab === tab.key ? t.emerald : "transparent"}`,
            marginBottom: -2, fontFamily: font.display, transition: "all 0.15s",
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══ OVERVIEW TAB ═══ */}
      {activeTab === "overview" && (
        <div style={{ display: "grid", gridTemplateColumns: responsive.settingsColumns, gap: 20 }}>
          {/* Left: Contact Info + Engagement */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Contact Info / Edit Form */}
            <Panel padding={24} style={{ position: "relative", overflow: "visible" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Contact Information</div>
              {/* Quick actions menu */}
              <div style={{ position: "absolute", top: 12, right: 12 }}>
                  <div onClick={() => setMenuOpen(menuOpen === "contact" ? false : "contact")} style={{
                    width: 30, height: 30, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", background: menuOpen === "contact" ? t.surfaceWarm : "transparent", border: `1px solid ${menuOpen === "contact" ? t.border : "transparent"}`,
                    transition: "all 0.15s",
                  }}
                    onMouseEnter={e => { if (menuOpen !== "contact") e.currentTarget.style.background = t.surfaceWarm; }}
                    onMouseLeave={e => { if (menuOpen !== "contact") e.currentTarget.style.background = "transparent"; }}
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="3" r="1.5" fill={t.textSecondary} />
                      <circle cx="8" cy="8" r="1.5" fill={t.textSecondary} />
                      <circle cx="8" cy="13" r="1.5" fill={t.textSecondary} />
                    </svg>
                  </div>
                  {menuOpen === "contact" && (
                    <>
                      <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setMenuOpen(false)} />
                      <div style={{
                        position: "absolute", top: 36, right: 0, background: t.surface,
                        border: `1.5px solid ${t.border}`, borderRadius: 10, padding: "6px 0",
                        minWidth: 180, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", zIndex: 100,
                        animation: "scaleIn 0.15s ease",
                      }}>
                        {[
                          { label: "Send Email", icon: "📧", action: () => { if (client.email) window.location.href = `mailto:${client.email}`; setMenuOpen(false); } },
                          { label: "Call", icon: "📞", action: () => { if (client.phone) window.location.href = `tel:${client.phone}`; setMenuOpen(false); }, disabled: !client.phone },
                          { label: "Create Deal", icon: "🤝", action: () => { navigate(`/deals/new?clientId=${client.id}`); setMenuOpen(false); } },
                        ].map((item, i) => (
                          <div key={i} onClick={item.disabled ? undefined : item.action} style={{
                            padding: "9px 16px", display: "flex", alignItems: "center", gap: 10,
                            cursor: item.disabled ? "default" : "pointer", fontSize: 13, fontFamily: font.body,
                            color: item.disabled ? t.textMuted : t.textPrimary,
                            opacity: item.disabled ? 0.4 : 1,
                            transition: "background 0.1s",
                          }}
                            onMouseEnter={e => { if (!item.disabled) e.currentTarget.style.background = t.surfaceWarm; }}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                          >
                            <span style={{ fontSize: 14 }}>{item.icon}</span>
                            <span>{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
              </div>
              {!editing ? (
                <div>
                  {[
                    { label: "Full Name", value: client.name },
                    { label: "Email", value: client.email },
                    { label: "Phone", value: client.phone },
                    { label: "Location", value: client.location },
                    { label: "Source", value: client.source },
                    { label: "Intent", value: client.intent },
                    { label: "Pre-Approved", value: client.mortgage ? "Yes" : "No" },
                    { label: "Created", value: new Date(client.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${t.border}`, fontSize: 13, fontFamily: font.body }}>
                      <span style={{ color: t.textMuted }}>{label}</span>
                      <span style={{ color: t.textPrimary, fontWeight: 500 }}>{value || "-"}</span>
                    </div>
                  ))}
                  {client.notes && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4, fontFamily: font.body, fontWeight: 600 }}>Notes</div>
                      <div style={{ fontSize: 13, color: t.textPrimary, lineHeight: 1.6, fontFamily: font.body, padding: "10px 14px", background: t.surfaceWarm, borderRadius: 8 }}>{client.notes}</div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {[
                    { key: "name", label: "Full Name" },
                    { key: "email", label: "Email" },
                    { key: "phone", label: "Phone" },
                    { key: "location", label: "Location" },
                  ].map(field => (
                    <div key={field.key} style={{ marginBottom: 12 }}>
                      <Input label={field.label} value={editForm[field.key] || ""} onChange={e => setEditForm({ ...editForm, [field.key]: e.target.value })} />
                    </div>
                  ))}
                  <div style={{ marginBottom: 12 }}>
                    <Textarea label="Notes" value={editForm.notes || ""} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} rows={3} />
                  </div>
                  <Button variant="primary" onClick={handleUpdate}>Save Changes</Button>
                </div>
              )}
            </Panel>

            {/* Assign to Agent */}
            {teamMembers.length > 0 && (
              <Panel padding={20}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 12, fontFamily: font.display }}>Assign to Agent</div>
                <select value={client.assigned_user || ""} onChange={e => handleAssign(e.target.value)}
                  style={{ width: "100%", boxSizing: "border-box", background: t.surfaceWarm, border: `1.5px solid ${t.border}`, borderRadius: 8, padding: "10px 12px", color: t.textPrimary, fontSize: 13, outline: "none", fontFamily: font.body }}>
                  <option value="">Unassigned</option>
                  {teamMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.name} {m.role === "team_manager" ? "(Manager)" : "(Agent)"}</option>
                  ))}
                </select>
                {client.assigned_name && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, padding: "8px 10px", background: t.emeraldLight, borderRadius: 8 }}>
                    <Avatar name={client.assigned_name} size={24} />
                    <span style={{ fontSize: 12, color: t.emerald, fontWeight: 600, fontFamily: font.body }}>{client.assigned_name}</span>
                  </div>
                )}
              </Panel>
            )}
          </div>

          {/* Right: Engagement + Quick Actions + Score */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* AI Score Breakdown */}
            <Panel padding={24}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>AI Client Score</div>
                <span style={{ fontSize: 28, fontWeight: 800, color: scoreColor, fontFamily: font.mono }}>{client.score || 0}%</span>
              </div>
              <ProgressBar value={client.score || 0} color={scoreColor} />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 14 }}>
                {[{ label: "Intent", value: client.intent }, { label: "Pre-Approved", value: client.mortgage ? "Yes" : "No" }, { label: "Source", value: client.source }].map(({ label, value }) => (
                  <div key={label} style={{ background: t.surfaceWarm, borderRadius: 8, padding: "10px 12px", textAlign: "center", border: `1px solid ${t.border}` }}>
                    <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, fontWeight: 600 }}>{label.toUpperCase()}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginTop: 3, fontFamily: font.body }}>{value || "-"}</div>
                  </div>
                ))}
              </div>

              {/* V1: 30-day score history sparkline — only renders when there are 2+ data points */}
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${t.border}` }}>
                <ScoreHistoryChart clientId={client.id} />
              </div>
            </Panel>

            {/* V4 Feature 4.4: Market context for the client's target area */}
            {client.location && (
              <MarketContext area={client.location} />
            )}

            {/* Engagement Status */}
            <Panel padding={24}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>Engagement Status</div>
                <span style={{
                  fontSize: 12, fontWeight: 700, fontFamily: font.mono, color: engColor,
                  padding: "3px 10px", borderRadius: 6, background: engColor + "15", border: `1px solid ${engColor}30`,
                }}>
                  {patternLabels[client.engagement_pattern] || "Active"}
                </span>
              </div>
              <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5, marginBottom: 12, fontFamily: font.body }}>
                {patternDescriptions[client.engagement_pattern] || patternDescriptions.active}
              </div>
              {client.status_reason && (
                <div style={{ fontSize: 11, color: t.textSecondary, fontStyle: "italic", marginBottom: 12, fontFamily: font.body, padding: "6px 10px", background: t.surfaceWarm, borderRadius: 6, border: `1px solid ${t.border}` }}>
                  {client.status_reason}
                </div>
              )}
              <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, fontFamily: font.body, marginBottom: 6 }}>Set Label:</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["active", "ghost_post_showing", "ghost_pre_showing", "ghost_post_offer", "seasonal_pauser", "slow_burner", "referred_out"].map(p => (
                  <div key={p} onClick={() => {
                    if (p === client.engagement_pattern) return;
                    if (p === "referred_out") {
                      const reason = window.prompt("Reason for referring out:");
                      if (reason !== null) handlePatternChange(p, reason || "Referred to another agent");
                    } else { handlePatternChange(p); }
                  }}
                    style={{
                      padding: "4px 10px", borderRadius: 6, fontSize: 10, cursor: p === client.engagement_pattern ? "default" : "pointer",
                      fontWeight: 600, fontFamily: font.body, transition: "all 0.15s",
                      background: p === client.engagement_pattern ? (patternColors[p] || t.emerald) + "25" : t.surface,
                      color: p === client.engagement_pattern ? patternColors[p] || t.emerald : t.textMuted,
                      border: `1px solid ${p === client.engagement_pattern ? (patternColors[p] || t.emerald) + "50" : t.border}`,
                    }}>
                    {patternLabels[p]}
                  </div>
                ))}
              </div>
            </Panel>

            {/* Quick Actions */}
            <Panel padding={24}>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>Quick Actions</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Button variant="primary" size="sm" onClick={() => navigate("/emails")}>
                  Send Email
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate(`/deals/new?client_id=${clientId}`)} style={{ background: `${t.sky}10`, borderColor: `${t.sky}40`, color: t.sky }}>
                  + New Deal
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate("/appointments")} style={{ background: `${t.amber}10`, borderColor: `${t.amber}40`, color: t.amber }}>
                  Schedule Showing
                </Button>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* ═══ DEALS TAB ═══ */}
      {activeTab === "deals" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>{deals.length} Deal{deals.length !== 1 ? "s" : ""}</div>
            <Button variant="primary" size="sm" onClick={() => navigate(`/deals/new?client_id=${clientId}`)}>+ New Deal</Button>
          </div>

          {deals.length === 0 ? (
            <Panel padding={60}>
              <div style={{ textAlign: "center" }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3, marginBottom: 12 }}>
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                </svg>
                <div style={{ fontSize: 14, color: t.textSecondary, fontFamily: font.body, marginBottom: 4 }}>No deals yet</div>
                <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>Create a deal to start tracking this client's pipeline</div>
              </div>
            </Panel>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {deals.map(deal => {
                const expanded = expandedDeal === deal.id;
                const cfg = stageColors[deal.status] || t.emerald;
                return (
                  <div key={deal.id} style={{
                    background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12,
                    borderLeft: `3px solid ${cfg}`, overflow: "hidden",
                  }}>
                    <div onClick={() => setExpandedDeal(expanded ? null : deal.id)} style={{
                      padding: "18px 22px", cursor: "pointer", display: "flex", alignItems: "center", gap: 16,
                      transition: "background 0.15s",
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>{deal.property_address || "TBD"}</div>
                        <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, marginTop: 2 }}>
                          {deal.type || "Purchase"} &middot; {deal.pipeline_type || "buyer"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        {deal.budget && <span style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.mono }}>{deal.budget}</span>}
                        <Badge variant={deal.status === "Closed" ? "emerald" : deal.status === "Offer Submitted" ? "purple" : deal.status === "Showing Scheduled" ? "amber" : "sky"}>{deal.status}</Badge>
                      </div>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ flexShrink: 0, transition: "transform 0.2s", transform: expanded ? "rotate(180deg)" : "rotate(0)" }}>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>

                    {expanded && (
                      <div style={{ padding: "0 22px 20px", borderTop: `1px solid ${t.border}` }}>
                        <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 10, paddingTop: 16, marginBottom: 14 }}>
                          {[{ l: "Budget", v: deal.budget }, { l: "Timeline", v: deal.timeline }, { l: "Type", v: deal.type }, { l: "Created", v: new Date(deal.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) }].map(d => (
                            <div key={d.l} style={{ background: t.surfaceWarm, borderRadius: 8, padding: "10px 12px", border: `1px solid ${t.border}` }}>
                              <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, fontWeight: 600 }}>{d.l.toUpperCase()}</div>
                              <div style={{ fontSize: 13, color: t.textPrimary, fontWeight: 600, marginTop: 3, fontFamily: font.body }}>{d.v || "-"}</div>
                            </div>
                          ))}
                        </div>
                        {deal.notes && <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 12, fontFamily: font.body }}>{deal.notes}</div>}
                        {deal.closed_at && <div style={{ fontSize: 11, color: t.emerald, marginBottom: 12, fontFamily: font.body }}>Closed: {new Date(deal.closed_at).toLocaleDateString()}</div>}

                        {/* Status buttons */}
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                          {["New Client", "Contacted", "Showing Scheduled", "Offer Submitted", "Closed"].map(s => (
                            <div key={s} onClick={() => handleDealStatusUpdate(deal.id, s)}
                              style={{
                                padding: "5px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer",
                                background: deal.status === s ? (stageColors[s] || t.emerald) + "20" : t.surface,
                                color: stageColors[s] || t.emerald,
                                border: `1px solid ${deal.status === s ? stageColors[s] : t.border}`, fontWeight: 600, fontFamily: font.body,
                              }}>{s}</div>
                          ))}
                        </div>
                        <div onClick={() => setConfirmDelete({ type: "deal", id: deal.id, name: deal.property_address || "this deal" })}
                          style={{ fontSize: 11, color: t.coral, cursor: "pointer", fontFamily: font.body }}>Remove deal</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ DOCUMENTS TAB ═══ */}
      {activeTab === "documents" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {/* Client-level document uploads */}
          <ClientDocuments clientId={clientId} />

          {/* Deal-specific documents */}
          {deals.length > 0 && (
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display, marginBottom: 12 }}>Deal Documents</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {deals.map(deal => (
                  <Panel key={deal.id} padding={20}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display, marginBottom: 4 }}>
                      {deal.property_address || "Deal #" + deal.id}
                    </div>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginBottom: 14 }}>
                      {deal.type || "Purchase"} &middot; {deal.status}
                    </div>
                    <DealDocumentTimeline dealId={deal.id} />
                  </Panel>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ AI TAB ═══ */}
      {activeTab === "ai" && (
        <AIPanel clientId={clientId} clientName={client.name} onStageApply={handleStageApply} onDataChange={fetchData} />
      )}

      {confirmDelete && (
        <ConfirmModal
          title={confirmDelete.type === "client" ? "Delete Contact" : "Delete Deal"}
          message={`Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            if (confirmDelete.type === "client") handleDeleteClient();
            else handleDeleteDeal(confirmDelete.id);
            setConfirmDelete(null);
          }}
        />
      )}
    </div>
  );
}
